import { useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import TeacherClassPicker from "@/components/TeacherClassPicker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquare } from "lucide-react";
import {
  classKey,
  classLabelFromKey,
  getRemarksForStudent,
  getSchoolState,
  getSessionUser,
  getStudentRecordForSession,
  getStudentsForClass,
  saveRemarkForStudent,
} from "@/lib/eisStore";
import { addRemark as addRemarkToSupabase, isSupabaseConfigured } from "@/lib/supabaseOps";

function StudentRemarks({ user }) {
  const studentRecord = getStudentRecordForSession(user);
  const studentClassKey = classKey(user.standard, user.division);
  const remarks = getRemarksForStudent(studentClassKey, studentRecord?.id);

  return (
    <AppShell>
      <div data-testid="remarks-page" className="space-y-6">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
          Remarks
        </h2>

        <Card>
          <CardHeader>
            <CardTitle style={{ color: "#0b1f7a" }}>
              Your Remarks
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {remarks.length ? (
              remarks.map((remark) => (
                <div key={remark.id} className="rounded-xl border border-slate-200 p-4">
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{remark.text}</p>
                  <p className="text-xs text-slate-500 mt-2">
                    {new Date(remark.createdAt).toLocaleString()}
                  </p>
                </div>
              ))
            ) : (
              <div className="p-12 flex flex-col items-center justify-center text-center rounded-xl border-2 border-dashed border-slate-300">
                <div
                  className="h-16 w-16 rounded-full flex items-center justify-center mb-4"
                  style={{ backgroundColor: "#EEF0FA" }}
                >
                  <MessageSquare size={28} style={{ color: "#0b1f7a" }} />
                </div>
                <p data-testid="remarks-empty" className="text-lg font-semibold text-slate-700">
                  no remarks yet
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

function TeacherRemarks({ user }) {
  const [teacherClassKey, setTeacherClassKey] = useState(user.assignedClassKey || "");
  const students = useMemo(
    () => (teacherClassKey ? getStudentsForClass(teacherClassKey) : []),
    [teacherClassKey],
  );
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [remarkText, setRemarkText] = useState("");
  const selectedStudent = students.find((student) => student.id === selectedStudentId) || students[0] || null;
  const selectedRemarks = selectedStudent ? getRemarksForStudent(teacherClassKey, selectedStudent.id) : [];

  const addRemark = async () => {
    if (!teacherClassKey) {
      toast.error("Please select a class first");
      return;
    }
    if (!selectedStudent) {
      toast.error("Please choose a student");
      return;
    }
    if (!remarkText.trim()) {
      toast.error("Please enter a remark");
      return;
    }

    if (isSupabaseConfigured) {
      try {
        await addRemarkToSupabase(selectedStudent.id, remarkText.trim(), "neutral");
      } catch (error) {
        console.error(error);
      }
    }

    saveRemarkForStudent(teacherClassKey, selectedStudent.id, {
      text: remarkText.trim(),
      teacherName: user.name,
    });
    setRemarkText("");
    toast.success("Remark added");
  };

  return (
    <AppShell>
      <div data-testid="remarks-page" className="space-y-6">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
          Remarks
        </h2>

        <TeacherClassPicker teacher={user} onChange={setTeacherClassKey} />

        <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-6">
          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>Student List</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {!teacherClassKey ? (
                <p className="text-sm text-slate-500">Select a class to see students.</p>
              ) : (
                students.map((student) => (
                  <button
                    key={student.id}
                    type="button"
                    onClick={() => setSelectedStudentId(student.id)}
                    className={`w-full text-left rounded-xl border px-4 py-3 transition ${
                      selectedStudent?.id === student.id
                        ? "border-[#0b1f7a] bg-[#EEF0FA]"
                        : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-800">{student.name}</p>
                        <p className="text-xs text-slate-500">
                          Roll {student.rollNo} · {student.standard} {student.division}
                        </p>
                      </div>
                      <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                        {getRemarksForStudent(teacherClassKey, student.id).length} remarks
                      </span>
                    </div>
                  </button>
                ))
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>Add Remarks</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-slate-500">Selected student</p>
                <p className="font-semibold text-slate-800">
                  {selectedStudent ? selectedStudent.name : "Choose a student from the list"}
                </p>
                <p className="text-xs text-slate-500">
                  {selectedStudent ? classLabelFromKey(teacherClassKey) : ""}
                </p>
              </div>

              <div>
                <Label>Remark</Label>
                <Textarea
                  value={remarkText}
                  onChange={(e) => setRemarkText(e.target.value)}
                  className="mt-1 min-h-32"
                  placeholder="Write your remark about the student"
                />
              </div>

              <Button
                type="button"
                onClick={addRemark}
                className="rounded-full text-white"
                style={{ backgroundColor: "#0b1f7a" }}
              >
                Save Remark
              </Button>

              <div className="space-y-3">
                <p className="text-sm font-semibold text-slate-700">Remark history</p>
                {selectedRemarks.length ? (
                  selectedRemarks.map((remark) => (
                    <div key={remark.id} className="rounded-xl border border-slate-200 p-4">
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{remark.text}</p>
                      <p className="text-xs text-slate-500 mt-2">
                        {new Date(remark.createdAt).toLocaleString()}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No remarks added yet.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

export default function Remarks() {
  const user = getSessionUser();
  if (user?.role === "teacher") {
    return <TeacherRemarks user={user} />;
  }
  return <StudentRemarks user={user || {}} />;
}
